import classNames from "classnames";
import { html } from "lit-html";
import i18next from "i18next";
import { useMemo } from "Shared/haunted/CustomHooks";
import { useMobileSearchboxModal } from "../mobile-searchbox-modal/useMobileSearchboxModal";
import { passengersUtils, PaxType, WarningKey } from "./passengersUtils";
import { infoIcon } from "../station-selector/newFlightSvgs";

export interface DisabledPassengerCountStatesDto {
	isAddAdultsDisabled: boolean;
	isAddChildrenDisabled: boolean;
	isAddInfantsDisabled: boolean;
	isAddTeensDisabled: boolean;
	isRemoveAdultsDisabled: boolean;
	isRemoveChildrenDisabled: boolean;
	isRemoveInfantsDisabled: boolean;
	isRemoveTeensDisabled: boolean;
}

export interface PassengerSelectorActionsDto {
	onConfirm: (e: MouseEvent) => void;
	onDecreasePaxNumber: (type: PaxType, isDisabled: boolean) => void;
	onIncreasePaxNumber: (type: PaxType, isDisabled: boolean) => void;
}

export interface WarningsDto {
	isChileanSearch: boolean;
	isColombianDomesticFlight: boolean;
	isWithGroupBookingWarning: boolean;
	isWithTeenInfantRemoveWarning: boolean;
}

export interface Props {
	actions: PassengerSelectorActionsDto;
	culture: string;
	disabledStates: DisabledPassengerCountStatesDto;
	passengers: {
		adults: number;
		teens: number;
		children: number;
		infants: number;
	};
	warnings: WarningsDto;
}

interface PassengerRowParams {
	type: PaxType;
	label: string;
	info: string;
	count: number;
	isRemoveDisabled: boolean;
	isAddDisabled: boolean;
}

export const useMobileSearchboxPassengerSelectorModal = (props: Props) => {
	const { createWarningsMap, getWarningsForCategory } = passengersUtils();
	const { adults, teens, children, infants } = props.passengers;

	const warningsMap = createWarningsMap(props.culture, props.warnings.isChileanSearch);

	const handleConfirmButtonClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		props.actions.onConfirm(e);
	};

	// TEMPLATES

	const amountTemplate = (amount: number) => html`<div class="text-lg font-bold text-n-blue">${amount}</div>`;

	const passengerTypeTemplate = (params: PassengerRowParams) => {
		const { type, label, info, count, isRemoveDisabled, isAddDisabled } = params;
		return html`
		<div class="flex items-center justify-between p-4">
			<div>
				<div class="font-body text-base font-bold text-n-blue">${label}</div>
				<div class="font-body text-sm text-[#a1a1a1]">${info}</div>
			</div>
			<div class="flex items-center justify-center gap-4">
				<ac-change-passenger-number-button
					.type=${"minus"}
					.isDisabled=${isRemoveDisabled}
					.onClick=${() => props.actions.onDecreasePaxNumber(type, isRemoveDisabled)}
				></ac-change-passenger-number-button>
				${amountTemplate(count)}
				<ac-change-passenger-number-button
					.type=${"plus"}
					.isDisabled=${isAddDisabled}
					.onClick=${() => props.actions.onIncreasePaxNumber(type, isAddDisabled)}
				></ac-change-passenger-number-button>
			</div>
		</div>
	`;
	};

	const warningsTemplate = (warnings: WarningKey[]) =>
		warnings.length > 0
			? html`
					<div class="flex flex-col gap-2">
						${warnings.map(
							(key) => html`
								<div class="flex items-center gap-3 rounded-lg bg-[#F0F3F7] px-2 py-3">
									<div>${infoIcon}</div>
									<div class="flex-1">
										<div class="font-body text-xs leading-relaxed text-n-blue">
											${warningsMap.get(key)}
										</div>
									</div>
								</div>
							`
						)}
					</div>
			  `
			: "";

	const passengerRowTemplate = (params: PassengerRowParams) => {
		const { type } = params;
		const warnings = getWarningsForCategory({
			type,
			passengers: props.passengers,
			isWithGroupBookingWarning: props.warnings.isWithGroupBookingWarning,
			isWithTeenInfantRemoveWarning: props.warnings.isWithTeenInfantRemoveWarning,
			isColombianDomesticFlight: props.warnings.isColombianDomesticFlight,
			culture: props.culture,
		});

		return html`
			<div
				class=${classNames("w-full", {
					"border-b border-solid border-[#e0e0e0]": type !== "infants",
					"pb-4": warnings.length > 0,
				})}
			>
				${passengerTypeTemplate(params)}
				${warningsTemplate(warnings)}
			</div>
		`;
	};

	const confirmButtonTemplate = () => html`
		<div
			class=${classNames(
				"flex cursor-pointer items-center justify-center rounded-full bg-n-cyan px-12 py-3",
				"font-body text-sm font-semibold text-white"
			)}
			@click=${handleConfirmButtonClick}
		>
			${i18next.t("Confirmar")} <i class="jsh-circle-chevron-right1 ml-2 mt-px"></i>
		</div>
	`;

	// DEVNOTE: Memorize content to avoid unnecessary re-renders
	const memoizedContentTemplate = useMemo(
		() => html`
			<div class="flex h-full flex-col gap-4 overflow-hidden">
				<div class="flex-1 overflow-y-auto">
					${passengerRowTemplate({
						type: "adults",
						label: i18next.t("Adultos"),
						info: i18next.t("18 o más años"),
						count: adults,
						isRemoveDisabled: props.disabledStates.isRemoveAdultsDisabled,
						isAddDisabled: props.disabledStates.isAddAdultsDisabled,
					})}
					${passengerRowTemplate({
						type: "teens",
						label: i18next.t("Adolescentes"),
						info: props.warnings.isChileanSearch ? i18next.t("14-17 años") : i18next.t("13-17 años"),
						count: teens,
						isRemoveDisabled: props.disabledStates.isRemoveTeensDisabled,
						isAddDisabled: props.disabledStates.isAddTeensDisabled,
					})}
					${passengerRowTemplate({
						type: "children",
						label: i18next.t("Niños"),
						info: props.warnings.isChileanSearch ? i18next.t("2-13 años") : i18next.t("2-12 años"),
						count: children,
						isRemoveDisabled: props.disabledStates.isRemoveChildrenDisabled,
						isAddDisabled: props.disabledStates.isAddChildrenDisabled,
					})}
					${passengerRowTemplate({
						type: "infants",
						label: i18next.t("Infantes"),
						info: i18next.t("0-23 meses"),
						count: infants,
						isRemoveDisabled: props.disabledStates.isRemoveInfantsDisabled,
						isAddDisabled: props.disabledStates.isAddInfantsDisabled,
					})}
				</div>
				<div class="flex shrink-0 items-center justify-center">${confirmButtonTemplate()}</div>
			</div>
		`,
		[
			adults,
			teens,
			children,
			infants,
			props.warnings.isWithGroupBookingWarning,
			props.warnings.isWithTeenInfantRemoveWarning,
			props.warnings.isColombianDomesticFlight,
			props.warnings.isChileanSearch,
			props.disabledStates,
		]
	);

	const modal = useMobileSearchboxModal({
		title: i18next.t("Elige los pasajeros"),
		content: memoizedContentTemplate,
	});

	return modal;
};
